import React, { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Dropdown, Layout, Space, Tooltip, Typography } from "antd";
import Splitter from "@/components/ui/splitter";
import { MenuItem } from ".";
import { ArrowLeftOutlined, AppstoreFilled, UserOutlined } from "@ant-design/icons";
import { View, useAppContext, useClientContext, useNavigation } from "@data-root/react";
import { ErrorView } from "@data-root/error-boundary";
import * as Sentry from "@sentry/react";
import { Menu } from "../menu";
import { Chat } from "./chat";
import { ContentSizeContext } from "@data-root/panels";
import { getEmbedModeFlag, getShowNavigationFlag } from "@/utils/tokenManager";

export type DesktopNavigationConfig = {
  headerLeftItems?: React.ReactNode[];
  headerCenterItems?: React.ReactNode[];
  headerRightItems?: React.ReactNode[];

  menu: MenuItem[];
};

const { Header, Content } = Layout;
const { Text } = Typography;

const initialCollapsed = localStorage.getItem("navigationCollapsed") === "true";

export const DesktopNavigatorComponent: FC<PropsWithChildren<DesktopNavigationConfig>> = ({
  children,
  headerLeftItems,
  headerCenterItems,
  headerRightItems,

  menu: menuItems,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const { navigation } = useNavigation();

  const { core, user } = useClientContext();
  const appContext = useAppContext();
  const { configuration, menu: _menu, app } = appContext;

  const [collapsed, _setCollapsed] = useState(initialCollapsed ?? configuration?.navigationCollapsed ?? true);
  const setCollapsed = useCallback((collapsed: boolean) => {
    localStorage.setItem("navigationCollapsed", collapsed.toString());
    _setCollapsed(collapsed);
  }, []);

  const onClickBack = useCallback(() => {
    // eslint-disable-next-line prefer-const
    let prevent = false;
    core.emitter.emit("back", core, () => (prevent = true));
    if (prevent) return;

    navigation.goBack();
  }, [core, navigation]);

  const menu = useMemo(
    () => ({
      items: [
        ...menuItems,
        {
          key: "__git_hash__",
          label: (
            <Text code copyable>
              {__GIT_HASH__}
            </Text>
          ),
          disabled: true,
        },
      ],
    }),
    [menuItems]
  );

  const [contentSize, setContentSize] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const { height } = contentRef.current.getBoundingClientRect();
    setContentSize(height);
  }, []);

  const navigationHidden = useMemo(() => configuration?.navigationHidden ?? false, [configuration?.navigationHidden]);
  const isEmbedMode = useMemo(() => getEmbedModeFlag(), []);
  const showNavigationInEmbed = useMemo(() => getShowNavigationFlag(), []);

  if (isEmbedMode) {
    if (showNavigationInEmbed) {
      return (
        <ContentSizeContext.Provider value={contentSize}>
          <Layout style={style}>
            <Content ref={contentRef} style={{ overflow: "auto" }}>
              <Splitter style={{ height: "100%" }}>
                <Splitter.Panel collapsible defaultSize="10%" min="10%" max="20%">
                  <Menu menu={_menu} />
                </Splitter.Panel>
                <Splitter.Panel defaultSize="90%">
                  <Sentry.ErrorBoundary fallback={ErrorView as any}>{children}</Sentry.ErrorBoundary>
                </Splitter.Panel>
              </Splitter>
            </Content>
          </Layout>
        </ContentSizeContext.Provider>
      );
    }
    return (
      <ContentSizeContext.Provider value={contentSize}>
        <Layout style={style}>
          <Content ref={contentRef} style={{ overflow: "auto" }}>
            <Sentry.ErrorBoundary fallback={ErrorView as any}>{children}</Sentry.ErrorBoundary>
          </Content>
        </Layout>
      </ContentSizeContext.Provider>
    );
  }

  return (
    <ContentSizeContext.Provider value={contentSize}>
      <Layout style={style}>
        <Content ref={contentRef} style={{ overflow: "auto" }}>
          <Splitter layout="vertical">
            <Splitter.Panel collapsible max={64} defaultSize={64}>
              <Header
                style={{
                  backgroundColor: appContext.styles.primaryColor,
                }}
              >
                <Space style={{ width: "100%", justifyContent: "space-between" }} direction="horizontal" align="center">
                  <Space>
                    <Dropdown menu={menu} arrow>
                      <Button
                        id="app-menu-button"
                        onClick={() => setCollapsed(!collapsed)}
                        type="link"
                        icon={<AppstoreFilled />}
                      >
                        {app.name}
                      </Button>
                    </Dropdown>

                    <Tooltip title="Atrás">
                      <Button
                        id="app-back-button"
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={onClickBack}
                        disabled={!navigation.canGoBack()}
                      />
                    </Tooltip>

                    {headerLeftItems}
                  </Space>

                  <View>{headerCenterItems}</View>

                  <Space
                    styles={{
                      item: {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    {headerRightItems}
                    <Tooltip title={user?.name} placement="bottom">
                      <Avatar icon={<UserOutlined />} />
                    </Tooltip>
                  </Space>
                </Space>
              </Header>
            </Splitter.Panel>

            <Splitter.Panel>
              <Splitter style={{ height: "100%" }}>
                {navigationHidden ? null : (
                  <Splitter.Panel collapsible defaultSize="10%" min="10%" max="20%">
                    <Menu menu={_menu} />
                  </Splitter.Panel>
                )}

                <Splitter.Panel defaultSize="90%">
                  <Sentry.ErrorBoundary fallback={ErrorView as any}>{children}</Sentry.ErrorBoundary>
                </Splitter.Panel>

                {/* <Splitter.Panel collapsible defaultSize="0%" min="10%" max="40%">
                  <Chat />
                </Splitter.Panel> */}
              </Splitter>
            </Splitter.Panel>
          </Splitter>
        </Content>
      </Layout>
    </ContentSizeContext.Provider>
  );
};

const style = {
  maxWidth: "100vw",
};
